import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ContractType } from './models';
import { baseApi } from '@centerpiece/components';

export const contract_type = baseApi
  .enhanceEndpoints({ addTagTypes: ['ContractTypes', 'ContractType'] })
  .injectEndpoints({
    endpoints: builder => ({
      getContractTypes: builder.query<ContractType[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'contract_type',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'ContractType' as const,
                  id,
                })),
                'ContractTypes',
              ]
            : ['ContractTypes'],
      }),
      getContractType: builder.query<ContractType, string>({
        query: id => ({
          url: `contract_type/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'ContractType', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createContractType: builder.mutation({
        query: request => ({
          url: 'contract_type',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['ContractTypes'],
      }),
      updateContractType: builder.mutation<ContractType, ContractType>({
        query: request => ({
          url: `contract_type/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'ContractType', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            contract_type.util.updateQueryData(
              'getContractType',
              `${id}`,
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(contract_type.util.invalidateTags(['ContractTypes']))
             */
            dispatch(contract_type.util.invalidateTags(['ContractTypes']));
          }
        },
      }),
      deleteContractType: builder.mutation<ContractType, number>({
        query: id => ({
          url: `contract_type/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'ContractType' as const, arg }, 'ContractTypes']
            : ['ContractTypes'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetContractTypesQuery,
  useGetContractTypeQuery,
  useCreateContractTypeMutation,
  useUpdateContractTypeMutation,
  useDeleteContractTypeMutation,
  usePrefetch: useContractTypesPrefetch,
} = contract_type;
const selectContractTypesRoot = contract_type.endpoints.getContractTypes.select(
  {}
);
export const selectContractTypes = createSelector(
  selectContractTypesRoot,
  contract_type => contract_type.data || []
);
